import { Buffer } from 'buffer'
import { store } from 'app/store'
import { Dashboard } from 'dashboard/page'
import { ForgotPassword } from 'auth/forgot-password'
import { Layout, LayoutWithoutReturnToDashboard } from 'app/layout'
import { Login } from 'auth/login'
import { ResetPassword } from 'auth/reset-password'
import NewPasswordRequired from 'auth/new-password-required'
import { Signup } from 'auth/signup'
import { Success } from 'auth/success'
import { Verification } from 'auth/verification'
import { EnterCode } from 'auth/enter-code'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { UpdateBanner } from 'components/update-banner'
import { ErrorBoundary } from 'components/error-boundary'
import { ScrollToTop } from 'components/scroll-to-top'
import { ToastHost } from 'components/toast'
import { installGlobalErrorHandlers } from 'util/error-reporting'
import { lazyPage } from 'util/lazy-page'
import 'css/common.css'
import 'css/fonts.css'
import 'css/print.css'


// Each screen below is its own downloadable file, fetched when first opened. Sign-in, the
// dashboard and the page frame stay in the main file because every visit starts there.
const Classroom = lazyPage(() => import('classes/classroom').then(m => ({ default: m.Classroom })))
const Classrooms = lazyPage(() => import('classes/classrooms').then(m => ({ default: m.Classrooms })))
const ComponentStaging = lazyPage(() => import('present-lesson/component-staging').then(m => ({ default: m.ComponentStaging })))
const LessonBuilder = lazyPage(() => import('lesson-builder/page').then(m => ({ default: m.LessonBuilder })))
const Lessons = lazyPage(() => import('lesson-library/page').then(m => ({ default: m.Lessons })))
const LessonOverviewPage = lazyPage(() => import('lesson-overview/page').then(m => ({ default: m.LessonOverviewPage })))
const QuestionResultsPage = lazyPage(() => import('question-results/page').then(m => ({ default: m.QuestionResultsPage })))
const Settings = lazyPage(() => import('settings/page').then(m => ({ default: m.Settings })))
const StudentOverviewPage = lazyPage(() => import('student-overview/page').then(m => ({ default: m.StudentOverviewPage })))
const StudentLessonResultsPage = lazyPage(() => import('student-lesson-results/page').then(m => ({ default: m.StudentLessonResultsPage })))
const UploadLessons = lazyPage(() => import('upload-lessons/page').then(m => ({ default: m.UploadLessons })))
const PresentLessonPage = lazyPage(() => import('present-lesson/page').then(m => ({ default: m.PresentLessonPage })))
const StudentLivePage = lazyPage(() => import('student-live/page').then(m => ({ default: m.StudentLivePage })))

window.Buffer = window.Buffer ?? Buffer

// Every uncaught error/rejection becomes visible (M-BIG3); must run before render
installGlobalErrorHandlers()

// Instant redirect: students go to /enter-code, teachers go to /dashboard
const HomeRedirect = () => {
	const isStudent = localStorage.getItem('studentId')
	return <Navigate to={isStudent ? '/enter-code' : '/dashboard'} replace />
}

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
	<Provider store={store}>
	<React.StrictMode>
		<BrowserRouter>
			<ScrollToTop />
			<ErrorBoundary label="root">
			<ToastHost />
			<UpdateBanner />
			<Suspense fallback={<div role="status" aria-live="polite" style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>}>
			<Routes>
				<Route path="/" element={<Layout />}>
					<Route path="settings" element={<Settings />} />
					<Route path="lessons" element={<Lessons />} />
					<Route path="upload-lessons" element={<UploadLessons />} />
					<Route path="lesson-builder" element={<LessonBuilder />} />
					<Route path="staging" element={<ComponentStaging />} />
					<Route path='classrooms' element={<Classrooms />} />
					<Route path='classrooms/:classId' element={<Classroom />} />
					<Route path='classrooms/:classId/lessons/:lessonId' element={<LessonOverviewPage />} />
					<Route path='classrooms/:classId/lessons/:lessonId/questions/:questionId' element={<QuestionResultsPage />} />
					<Route path='classrooms/:classId/students/:studentId' element={<StudentOverviewPage />} />
					<Route path='classrooms/:classId/students/:studentId/lessons/:lessonId' element={<StudentLessonResultsPage />} />
				</Route>
				<Route path="/" element={<LayoutWithoutReturnToDashboard />}>
					<Route index element={<HomeRedirect />} />
					<Route path="new-password-required" element={<NewPasswordRequired />} />
					<Route path="dashboard" element={<Dashboard />} />
					<Route path="login" element={<Login />} />
					<Route path="verification" element={<Verification />} />
					<Route path="signup" element={<Signup />} />
					<Route path="success" element={<Success />} />
					<Route path="forgot-password" element={<ForgotPassword />} />
					<Route path="reset-password" element={<ResetPassword />} />
				</Route>
				<Route path="/enter-code" element={<EnterCode />} />
				{/* Live pages get their own boundaries: a render error mid-class
				    must never take down more than the page it happened on */}
				<Route path="/present/:classId/:lessonId" element={<ErrorBoundary label="present-lesson"><PresentLessonPage /></ErrorBoundary>} />
				<Route path="/live/:sessionCode" element={<ErrorBoundary label="student-live"><StudentLivePage /></ErrorBoundary>} />
			</Routes>
			</Suspense>
			</ErrorBoundary>
		</BrowserRouter>
	</React.StrictMode>
	</Provider>
)
